import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getAppConfig } from '@/services/config';
import { AppConfig } from '@/types/config';

interface ConfigContextType {
    config: AppConfig | null;
    isLoading: boolean;
}

/** Hard-coded defaults so the logo always works even when the API is unreachable */
const DEFAULT_CONFIG: AppConfig = {
    school_name: 'TaleemX',
    school_logo_url: '/logo-mark.svg'
};

const ConfigContext = createContext<ConfigContextType>({ config: null, isLoading: true });

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
    // Start with defaults so the UI is never in a state with null config
    const [config, setConfig] = useState<AppConfig | null>(DEFAULT_CONFIG);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getAppConfig()
            .then((apiConfig) => {
                const apiLogo = apiConfig?.school_logo_url;
                const isDefaultLogo =
                    !apiLogo ||
                    apiLogo === '/logo.png' ||
                    apiLogo === '/logo-taleem.png' ||
                    apiLogo === '/logo-taleem.jpg' ||
                    apiLogo === '/logo-pen.svg' ||
                    apiLogo === '/logo-mark.svg' ||
                    apiLogo === '/logo-taleemx.svg';

                setConfig({
                    ...DEFAULT_CONFIG,
                    ...apiConfig,
                    school_name: apiConfig?.school_name || DEFAULT_CONFIG.school_name,
                    school_logo_url: isDefaultLogo ? DEFAULT_CONFIG.school_logo_url : apiLogo,
                });
            })
            .catch((err) => {
                console.error('Failed to load app config, using defaults', err);
                setConfig(DEFAULT_CONFIG);
            })
            .finally(() => setIsLoading(false));
    }, []);

    return (
        <ConfigContext.Provider value={{ config, isLoading }}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = () => useContext(ConfigContext);
